import React, { useState } from 'react';
import api from '../api/api';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CalendarCheck, CalendarPlus, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const fmt = (iso) =>
  new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

/** Cliente: fissa, conferma o modifica (una sola volta) la data della prestazione. */
export const AppointmentCard = ({ request, onUpdated }) => {
  const [value, setValue] = useState(
    request.appointment_at ? request.appointment_at.slice(0, 16) : ''
  );
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  if (!['accepted', 'in_progress'].includes(request.status)) return null;

  const changes = request.appointment_changes_count ?? 0;
  const locked = !!request.appointment_at && changes >= 1;
  const proposed = request.proposed_appointment_at;

  const save = async () => {
    if (!value) { toast.error('Scegli data e ora'); return; }
    setSaving(true);
    try {
      await api.post(`/api/requests/${request.request_id}/appointment`, { appointment_at: value });
      toast.success('Data inviata al professionista');
      setEditing(false);
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio data');
    }
    setSaving(false);
  };

  const confirmProposed = async () => {
    setSaving(true);
    try {
      await api.post(`/api/requests/${request.request_id}/appointment/confirm`);
      toast.success('Data confermata');
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore conferma');
    }
    setSaving(false);
  };

  return (
    <Card className="rounded-xl p-4 mb-4 bg-sky-50 border border-sky-200" data-testid="appointment-card">
      {request.appointment_at && !editing ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sky-900 font-medium">
              <CalendarCheck size={20} weight="duotone" /> Prestazione fissata
            </div>
            <div className="text-lg font-heading font-semibold text-sky-800 mt-1" data-testid="appointment-when">
              {fmt(request.appointment_at)}
            </div>
            <div className="text-xs text-sky-900/70 mt-1">
              {locked
                ? 'Data bloccata: hai già usato la modifica disponibile'
                : 'Puoi modificarla una sola volta'}
            </div>
          </div>
          {!locked && (
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => setEditing(true)} data-testid="appointment-edit-btn">
              Modifica data
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sky-900 font-medium">
            <CalendarPlus size={20} weight="duotone" /> Indica la data della prestazione
          </div>
          <p className="text-sm text-sky-900/80">
            Il professionista ha accettato la tua richiesta. Indica data e ora: solo dopo la conferma
            potrà contattarti. Se non la indichi entro 3 giorni la richiesta decade.
          </p>
          {proposed && (
            <div className="p-3 rounded-lg bg-white border border-sky-200 flex flex-wrap items-center justify-between gap-2" data-testid="appointment-proposed">
              <div className="text-sm">
                Il professionista propone <strong>{fmt(proposed)}</strong>
              </div>
              <Button size="sm" className="rounded-full" onClick={confirmProposed} disabled={saving} data-testid="appointment-confirm-btn">
                Confermo questa data
              </Button>
            </div>
          )}
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs">Data e ora</Label>
              <Input
                type="datetime-local"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                data-testid="appointment-input"
              />
            </div>
            <Button className="rounded-full" onClick={save} disabled={saving} data-testid="appointment-save-btn">
              {saving ? 'Invio…' : request.appointment_at ? 'Salva nuova data' : 'Invia data'}
            </Button>
            {editing && (
              <Button variant="ghost" className="rounded-full" onClick={() => setEditing(false)} data-testid="appointment-cancel-edit">
                Annulla
              </Button>
            )}
          </div>
          {request.appointment_at && (
            <p className="text-xs text-amber-800 flex items-center gap-1">
              <Warning size={14} /> Dopo questa modifica la data resterà bloccata.
            </p>
          )}
        </div>
      )}
    </Card>
  );
};

export default AppointmentCard;
